// The message-key table — `data/tables/message-keys.json` typed once at load (ARCH §2.7, API §1.7).
// `MessageKey` is derived from the JSON, so a key that is not in the table is a type error wherever a key is
// written in TypeScript (the catalogue, the demo server, tests); the wire (`Message.key: string`) is validated
// at render time instead. `message(key, params)` is the typed constructor for anything that emits messages.

import type { Message, MessageParam } from '../types/primitives';
import table from '../../data/tables/message-keys.json';
import { detectDev } from './env';

/** The `t` of a `MessageParam` — the wire type of a param (API §12.1). */
export type MessageParamType = MessageParam['t'];

/** Every key a server may emit. Derived from the table, never spelled by hand. */
export type MessageKey = keyof typeof table.keys;

/** The declared param names of one key (`never` when the key has none). */
export type MessageParamNames<K extends MessageKey> = keyof (typeof table)['keys'][K]['params'] &
  string;

/** The params a key requires, each a `MessageParam` of the type the table declares (checked at render). */
export type MessageParamsFor<K extends MessageKey> = {
  [P in MessageParamNames<K>]: MessageParam;
};

/** One row of the table. */
export interface MessageKeySpec {
  /**
   * The declared wire type(s) of each param. Always a LIST here, even where the table writes a single
   * string: a param may declare a SET of acceptable types (API §1.7, 0.3.13) for the one case where the
   * data decides which — a threshold `limit` is `money` on a money metric, `pct` on a rate and `number` on
   * a count. The renderer accepts a declared type and refuses every other, so the set is as closed as a
   * single type is.
   */
  params: Readonly<Record<string, readonly MessageParamType[]>>;
  /** Where the words come from (a requirement, an engine rule, a story row). */
  req?: string;
  /** `"V1"`: the MVP never renders it (kept so a preset family stays complete). */
  release?: string;
  /** A demo build never renders it (REQ-283 AC5); SC-5 scans it separately. */
  liveOnly?: boolean;
}

const PARAM_TYPES: readonly MessageParamType[] = [
  'money',
  'number',
  'pct',
  'count',
  'date',
  'instant',
  'window',
  'entity',
  'resultType',
  'message',
  'metric',
  'text',
];

function isParamType(value: unknown): value is MessageParamType {
  return typeof value === 'string' && (PARAM_TYPES as readonly string[]).includes(value);
}

/** A row's param value: one type, or a non-empty set of them (0.3.13). Duplicates are a table bug. */
function parseParamTypes(key: string, name: string, raw: unknown): MessageParamType[] {
  const list = Array.isArray(raw) ? raw : [raw];
  if (list.length === 0)
    throw new Error(`message-keys.json: "${key}" param "${name}" declares no type`);
  const types: MessageParamType[] = [];
  for (const type of list) {
    if (!isParamType(type))
      throw new Error(
        `message-keys.json: "${key}" param "${name}" has unknown type "${String(type)}"`,
      );
    if (types.includes(type))
      throw new Error(`message-keys.json: "${key}" param "${name}" repeats type "${type}"`);
    types.push(type);
  }
  return types;
}

function parseSpec(key: string, row: unknown): MessageKeySpec {
  if (!row || typeof row !== 'object' || !('params' in row) || typeof row.params !== 'object')
    throw new Error(`message-keys.json: "${key}" has no params object`);
  const params: Record<string, readonly MessageParamType[]> = {};
  for (const [name, type] of Object.entries(row.params as Record<string, unknown>)) {
    if (!/^[a-z][A-Za-z0-9]*$/.test(name))
      throw new Error(`message-keys.json: "${key}" param "${name}" is not a camelCase name`);
    params[name] = parseParamTypes(key, name, type);
  }
  const spec: MessageKeySpec = { params };
  if ('req' in row && typeof row.req === 'string') spec.req = row.req;
  if ('release' in row && typeof row.release === 'string') spec.release = row.release;
  if ('liveOnly' in row && row.liveOnly === true) spec.liveOnly = true;
  return spec;
}

/** The raw shape of `message-keys.json`'s `keys` object — what `validateMessageKeysTable` checks. */
export type MessageKeysTable = Readonly<Record<string, unknown>>;

/**
 * The table's rules, every row checked: a dotted lower_snake key, a `params` object whose names are camelCase and
 * whose types are the wire's, at least one key. Throws on the first breach, naming it. Run at module load under
 * development and test (`detectDev`), by `messages.test.ts` / `keys.test.ts` on the committed table and on broken
 * ones — and NOT in a production bundle, where the 448 regex tests were eager work on the first screen for a file
 * the test suite had already proved (T-322, D-772's finding 8.1).
 */
export function validateMessageKeysTable(
  keys: MessageKeysTable,
): ReadonlyMap<string, MessageKeySpec> {
  const specs = new Map<string, MessageKeySpec>();
  for (const [key, row] of Object.entries(keys)) {
    if (!/^[a-z][a-z0-9_]*(\.[a-z0-9][a-z0-9_]*)+$/.test(key))
      throw new Error(`message-keys.json: "${key}" is not a dotted lower_snake key`);
    specs.set(key, parseSpec(key, row));
  }
  if (specs.size === 0) throw new Error('message-keys.json: no keys');
  return specs;
}

/**
 * The table typed as its rows already are — the production path, no check (the JSON is the committed, tested
 * one). The one thing it does do is NORMALISE a param's declared type to a list, because the table writes a
 * bare string for the common single-type case and the renderer reads a list (0.3.13). That is an
 * `Array.isArray` per param and no regex, which is what this path exists to avoid (T-322, D-772's 8.1).
 */
function trustTable(keys: MessageKeysTable): ReadonlyMap<MessageKey, MessageKeySpec> {
  const specs = new Map<MessageKey, MessageKeySpec>();
  for (const [key, row] of Object.entries(keys) as [MessageKey, MessageKeySpec][]) {
    const params: Record<string, readonly MessageParamType[]> = {};
    for (const [name, type] of Object.entries(row.params as Readonly<Record<string, unknown>>))
      params[name] = (Array.isArray(type) ? type : [type]) as readonly MessageParamType[];
    specs.set(key, { ...row, params });
  }
  return specs;
}

/** `ruleVersion` of the table — bumped when a key or param changes shape. */
export const MESSAGE_KEYS_RULE_VERSION: number = table.ruleVersion;

const SPECS: ReadonlyMap<MessageKey, MessageKeySpec> = detectDev()
  ? (validateMessageKeysTable(table.keys) as ReadonlyMap<MessageKey, MessageKeySpec>)
  : trustTable(table.keys);

/** Every key in table order. */
export const MESSAGE_KEYS: readonly MessageKey[] = [...SPECS.keys()];

/** The words a `count` param's `unit` may be, besides any `ResultTypeKey`. */
export const COUNT_UNITS: readonly string[] = table.units.words;

/** Is this string a key of the table? */
export function isMessageKey(key: string): key is MessageKey {
  return SPECS.has(key as MessageKey);
}

/** The table row of a key, or `undefined` for a key that is not in the table. */
export function messageKeySpec(key: string): MessageKeySpec | undefined {
  return SPECS.get(key as MessageKey);
}

/** The group of a key — the part before the first dot (`status`, `alert`, …). */
export function messageKeyGroup(key: MessageKey): string {
  return key.slice(0, key.indexOf('.'));
}

/**
 * T-311 (D-700, BUG-148's guard) — the keys the table marks `release: "V1"`: the server keeps them (a preset family
 * stays complete; the server's name for a preset not on the wire, D-222) and NO MVP screen renders them. Derived
 * from the table, never spelled by hand, so marking a key `V1` in `message-keys.json` is the whole act.
 */
export const V1_MESSAGE_KEYS: ReadonlySet<MessageKey> = new Set(
  [...SPECS].filter(([, spec]) => spec.release === 'V1').map(([key]) => key),
);

/** Is this a `release: "V1"` key — one an MVP screen must not put on screen? A key not in the table is `false`. */
export function isV1MessageKey(key: string): boolean {
  return V1_MESSAGE_KEYS.has(key as MessageKey);
}

/**
 * The English of every V1 key as the catalogue holds it (`en.messages[key]`, templates with their `{slots}`), for
 * an app's glossary test: a rendered MVP screen may contain none of these sentences. Takes the catalogue rather than
 * importing it so this module stays the table's and `en.ts` keeps importing it.
 */
export function v1MessageTemplates(
  catalogue: Readonly<Record<string, string>>,
): ReadonlyArray<{ key: MessageKey; template: string }> {
  return [...V1_MESSAGE_KEYS].map((key) => ({ key, template: catalogue[key] ?? '' }));
}

type ParamsArg<K extends MessageKey> = [MessageParamNames<K>] extends [never]
  ? [params?: Record<string, never>]
  : [params: MessageParamsFor<K>];

/**
 * The typed constructor: `message('alert.fired.no_results', { entity, spent, window, result })` — a typo in
 * the key or a missing param name is a type error. Param TYPES are the wire's and are checked at render.
 */
export function message<K extends MessageKey>(key: K, ...rest: ParamsArg<K>): Message {
  return { key, params: (rest[0] ?? {}) as Record<string, MessageParam> };
}
